import React, { useRef } from "react";
import { styled, Theme } from "@mui/system";
import {
  Typography,
  Button,
  Grid,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ReactRouterLink from "./ReactRouterLink";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToElement } from "../../utils";

// Icons
import VerifiedIcon from "@mui/icons-material/Verified";
import GppGoodIcon from "@mui/icons-material/GppGood";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";

const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.65,
      ease: "easeOut",
    },
  },
};

const fadeInRight = {
  hidden: {
    opacity: 0,
    x: 30,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      delay: 0.15,
      ease: "easeOut",
    },
  },
};

const HeroContainer = styled("section")(({ theme }: { theme: Theme }) => ({
  position: "relative",
  minHeight: "650px",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  color: "#ffffff",
  backgroundColor: "#17181b",
  padding: theme.spacing(8, 4),

  [theme.breakpoints.down("lg")]: {
    minHeight: "610px",
    padding: theme.spacing(7, 3),
  },

  [theme.breakpoints.down("sm")]: {
    minHeight: "auto",
    padding: theme.spacing(6, 2, 5),
  },
}));

const ParallaxBackground = styled(motion.div)({
  position: "absolute",
  top: "-90px",
  right: 0,
  bottom: "-90px",
  left: 0,
  backgroundImage: `url("/fp-hero-bkg.jpeg")`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  transform: "scale(1.08)",
  willChange: "transform",
});

const BackgroundOverlay = styled(Box)(({ theme }: { theme: Theme }) => ({
  position: "absolute",
  inset: 0,
  background: `
      linear-gradient(
        90deg,
        rgba(13, 15, 18, 0.96) 0%,
        rgba(13, 15, 18, 0.88) 43%,
        rgba(13, 15, 18, 0.55) 72%,
        rgba(13, 15, 18, 0.38) 100%
      )
    `,

  [theme.breakpoints.down("lg")]: {
    background: `
        linear-gradient(
          90deg,
          rgba(13, 15, 18, 0.94) 0%,
          rgba(13, 15, 18, 0.86) 62%,
          rgba(13, 15, 18, 0.68) 100%
        )
      `,
  },

  [theme.breakpoints.down("sm")]: {
    background: `
        linear-gradient(
          rgba(13, 15, 18, 0.82),
          rgba(13, 15, 18, 0.94)
        )
      `,
  },
}));

const BackgroundPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.16,
  backgroundImage: `
    linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)
  `,
  backgroundSize: "52px 52px",
  maskImage: "linear-gradient(to right, rgba(0,0,0,0.75), transparent 78%)",
  pointerEvents: "none",
});

const OrangeGlow = styled(Box)({
  position: "absolute",
  width: "520px",
  height: "520px",
  right: "-190px",
  top: "-220px",
  borderRadius: "50%",
  background:
    "radial-gradient(circle, rgba(239,132,78,0.38) 0%, rgba(239,132,78,0) 70%)",
  pointerEvents: "none",
});

const BlueGlow = styled(Box)({
  position: "absolute",
  width: "440px",
  height: "440px",
  left: "-210px",
  bottom: "-250px",
  borderRadius: "50%",
  background:
    "radial-gradient(circle, rgba(57,142,211,0.2) 0%, rgba(57,142,211,0) 72%)",
  pointerEvents: "none",
});

const HeroInner = styled(Box)({
  position: "relative",
  zIndex: 2,
  width: "100%",
  maxWidth: "1220px",
  margin: "0 auto",
});

const HeroLayout = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.25fr) minmax(320px, 0.75fr)",
  alignItems: "center",
  gap: theme.spacing(7),

  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 0,
  },
}));

const ContentColumn = styled(motion.div)(({ theme }: { theme: Theme }) => ({
  width: "100%",
  maxWidth: "720px",

  [theme.breakpoints.down("lg")]: {
    maxWidth: "760px",
  },

  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    textAlign: "center",
    margin: "0 auto",
  },
}));

const Eyebrow = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.8),
  padding: theme.spacing(0.85, 1.5),
  marginBottom: theme.spacing(2.25),
  borderRadius: "999px",
  background:
    "linear-gradient(90deg, rgba(239,132,78,0.3), rgba(255,255,255,0.09))",
  border: "1px solid rgba(255,255,255,0.2)",
  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  backdropFilter: "blur(12px)",
}));

const AccentText = styled("span")({
  position: "relative",
  display: "inline-block",
  color: "#ffa176",

  "&::after": {
    content: '""',
    position: "absolute",
    left: "1%",
    right: "1%",
    bottom: "-5px",
    height: "6px",
    borderRadius: "999px",
    background:
      "linear-gradient(90deg, rgba(239,132,78,0.95), rgba(255,183,136,0.28))",
    transform: "rotate(-1deg)",
  },
});

const StartSellingButton = styled(Button)(({ theme }: { theme: Theme }) => ({
  minHeight: "52px",
  padding: theme.spacing(1.35, 3.25),
  borderRadius: "999px",
  color: "#ffffff",
  fontSize: "1rem",
  fontWeight: 800,
  textTransform: "none",
  background: "linear-gradient(135deg, #f49a6d 0%, #e87945 100%)",
  boxShadow: "0 13px 30px rgba(226,112,61,0.4)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",

  "&:hover": {
    color: "#ffffff",
    background: "linear-gradient(135deg, #f7a77e 0%, #db6b38 100%)",
    boxShadow: "0 17px 36px rgba(226,112,61,0.48)",
    transform: "translateY(-2px)",
  },
}));

const BrowseToysButton = styled(Button)(({ theme }: { theme: Theme }) => ({
  minHeight: "52px",
  padding: theme.spacing(1.35, 3.25),
  borderRadius: "999px",
  color: "#ffffff",
  border: "1px solid rgba(255,255,255,0.48)",
  fontSize: "1rem",
  fontWeight: 750,
  textTransform: "none",
  backgroundColor: "rgba(255,255,255,0.09)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
  backdropFilter: "blur(10px)",
  transition:
    "transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",

  "&:hover": {
    color: "#ffffff",
    borderColor: "rgba(255,255,255,0.85)",
    backgroundColor: "rgba(255,255,255,0.16)",
    transform: "translateY(-2px)",
  },
}));

const FreeListingNote = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.8),
  marginTop: theme.spacing(2),
  color: "rgba(255,255,255,0.8)",

  [theme.breakpoints.down("sm")]: {
    justifyContent: "center",
    width: "100%",
  },
}));

const TrustBar = styled(Box)(({ theme }: { theme: Theme }) => ({
  width: "100%",
  maxWidth: "720px",
  marginTop: theme.spacing(4),
  padding: theme.spacing(2.25),
  borderRadius: "20px",
  border: "1px solid rgba(255,255,255,0.16)",
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.055))",
  boxShadow: "0 20px 48px rgba(0,0,0,0.22)",
  backdropFilter: "blur(14px)",

  [theme.breakpoints.down("sm")]: {
    marginTop: theme.spacing(3.25),
    padding: theme.spacing(2),
  },
}));

const TrustItem = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),

  "& .MuiSvgIcon-root": {
    color: "#ffa176",
  },

  [theme.breakpoints.down("sm")]: {
    alignItems: "flex-start",
  },
}));

const DesktopCardsColumn = styled(motion.div)(
  ({ theme }: { theme: Theme }) => ({
    position: "relative",
    minHeight: "430px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-end",

    [theme.breakpoints.down("lg")]: {
      display: "none",
    },
  }),
);

const MarketplaceCard = styled(Box)(({ theme }: { theme: Theme }) => ({
  width: "310px",
  padding: theme.spacing(2.5),
  borderRadius: "24px",
  color: "#25262b",
  backgroundColor: "rgba(255,255,255,0.96)",
  boxShadow: "0 28px 65px rgba(0,0,0,0.34)",
  backdropFilter: "blur(16px)",
  transform: "rotate(1.5deg)",
}));

const FloatingTrustCard = styled(Box)(({ theme }: { theme: Theme }) => ({
  position: "absolute",
  left: "-10px",
  top: "25px",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.25),
  padding: theme.spacing(1.4, 1.75),
  borderRadius: "17px",
  color: "#ffffff",
  backgroundColor: "rgba(30,30,35,0.78)",
  border: "1px solid rgba(255,255,255,0.18)",
  boxShadow: "0 18px 42px rgba(0,0,0,0.28)",
  backdropFilter: "blur(14px)",
  transform: "rotate(-2deg)",
}));

const CardIconBox = styled(Box)({
  width: "42px",
  height: "42px",
  flexShrink: 0,
  borderRadius: "13px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#ffffff",
  background:
    "linear-gradient(135deg, rgba(244,154,109,1), rgba(226,112,61,1))",
  boxShadow: "0 8px 20px rgba(226,112,61,0.3)",
});

const SellingBadge = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.6),
  padding: theme.spacing(0.6, 1.1),
  marginBottom: theme.spacing(1.6),
  borderRadius: "999px",
  color: "#b85c33",
  backgroundColor: "#fff0e8",
}));

const HeroComponent: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const heroRef = useRef<HTMLElement | null>(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], [0, 500]);

  const contentY = useTransform(scrollYProgress, [0, 1], [0, -18]);

  const handleScrollToProducts = () => {
    scrollToElement("products-section");
  };

  return (
    <HeroContainer ref={heroRef}>
      <ParallaxBackground style={{ y: backgroundY }} />

      <BackgroundOverlay />
      <BackgroundPattern />
      <OrangeGlow />
      <BlueGlow />

      <HeroInner>
        <HeroLayout>
          <ContentColumn
            style={{ y: contentY }}
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
          >
            <Eyebrow>
              <AutoAwesomeIcon
                sx={{
                  fontSize: "1rem",
                  color: "#ffa176",
                }}
              />

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                }}
              >
                THE TOY MARKETPLACE
              </Typography>
            </Eyebrow>

            <Typography
              component="h1"
              sx={{
                maxWidth: "720px",
                fontSize: {
                  xs: "2.25rem",
                  sm: "3.45rem",
                  md: "4.35rem",
                },
                lineHeight: {
                  xs: 1.08,
                  sm: 1.035,
                },
                fontWeight: 900,
                letterSpacing: "-0.045em",
                mb: 2.5,
                textShadow: "0 5px 25px rgba(0,0,0,0.34)",
              }}
            >
              Find toys you’ll love.{" "}
              <AccentText>Sell toys ready for a new home.</AccentText>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: "640px",
                fontSize: {
                  xs: "1.02rem",
                  sm: "1.2rem",
                },
                lineHeight: 1.7,
                color: "rgba(255,255,255,0.86)",
                mb: 3.5,
                mx: isSmallScreen ? "auto" : 0,
              }}
            >
              Shop toys, collectibles, and childhood favorites from sellers
              across the country. Have toys of your own? Create a listing in
              minutes and connect with buyers looking for them.
            </Typography>

            <Grid
              container
              spacing={1.5}
              justifyContent={isSmallScreen ? "center" : "flex-start"}
            >
              <Grid item xs={12} sm="auto">
                <ReactRouterLink
                  to="/blog/start-selling-on-fiddlepiddlecom"
                  isLinkUnderline={false}
                  linkElement={
                    <StartSellingButton
                      variant="contained"
                      fullWidth={isSmallScreen}
                      endIcon={<ArrowForwardRoundedIcon />}
                    >
                      Start Selling
                    </StartSellingButton>
                  }
                />
              </Grid>

              <Grid item xs={12} sm="auto">
                <BrowseToysButton
                  variant="outlined"
                  fullWidth={isSmallScreen}
                  onClick={handleScrollToProducts}
                  startIcon={<SearchRoundedIcon />}
                >
                  Browse Toys
                </BrowseToysButton>
              </Grid>
            </Grid>

            <FreeListingNote>
              <StarRoundedIcon
                sx={{
                  fontSize: "1rem",
                  color: "#ffa176",
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 650,
                }}
              >
                Free to list. Simple to sell. Made for toys.
              </Typography>
            </FreeListingNote>

            <TrustBar>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <TrustItem>
                    <VerifiedIcon fontSize="small" />

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#ffffff",
                          fontWeight: 750,
                          lineHeight: 1.2,
                        }}
                      >
                        Verified
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.58)",
                          lineHeight: 1.2,
                        }}
                      >
                        Seller accounts
                      </Typography>
                    </Box>
                  </TrustItem>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <TrustItem>
                    <GppGoodIcon fontSize="small" />

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#ffffff",
                          fontWeight: 750,
                          lineHeight: 1.2,
                        }}
                      >
                        Protected
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.58)",
                          lineHeight: 1.2,
                        }}
                      >
                        Secure payments
                      </Typography>
                    </Box>
                  </TrustItem>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <TrustItem>
                    <RocketLaunchIcon fontSize="small" />

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#ffffff",
                          fontWeight: 750,
                          lineHeight: 1.2,
                        }}
                      >
                        Fast Setup
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.58)",
                          lineHeight: 1.2,
                        }}
                      >
                        List in minutes
                      </Typography>
                    </Box>
                  </TrustItem>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <TrustItem>
                    <LocalShippingIcon fontSize="small" />

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#ffffff",
                          fontWeight: 750,
                          lineHeight: 1.2,
                        }}
                      >
                        Easy Shipping
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.58)",
                          lineHeight: 1.2,
                        }}
                      >
                        Simple fulfillment
                      </Typography>
                    </Box>
                  </TrustItem>
                </Grid>
              </Grid>
            </TrustBar>
          </ContentColumn>

          <DesktopCardsColumn
            variants={fadeInRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
          >
            <FloatingTrustCard>
              <CardIconBox>
                <VerifiedIcon sx={{ fontSize: "1.3rem" }} />
              </CardIconBox>

              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  Built for toy lovers
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    color: "rgba(255,255,255,0.63)",
                  }}
                >
                  A marketplace focused on toys
                </Typography>
              </Box>
            </FloatingTrustCard>

            <MarketplaceCard>
              <SellingBadge>
                <SellRoundedIcon sx={{ fontSize: "1rem" }} />

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                  }}
                >
                  SELLING MADE SIMPLE
                </Typography>
              </SellingBadge>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 850,
                  lineHeight: 1.2,
                  mb: 1.25,
                }}
              >
                Turn unused toys into something valuable.
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#686971",
                  lineHeight: 1.6,
                  mb: 2,
                }}
              >
                Create your account, add your toy, and reach buyers searching
                for exactly what you have.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: "#b85c33",
                }}
              >
                <FavoriteRoundedIcon sx={{ fontSize: "1rem" }} />

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                  }}
                >
                  Give every toy another chance
                </Typography>
              </Box>
            </MarketplaceCard>
          </DesktopCardsColumn>
        </HeroLayout>
      </HeroInner>
    </HeroContainer>
  );
};

export default HeroComponent;
